// Implements: F-DSG-001, F-UIX-020
/**
 * "Borrar perfil" of the open profile (owner 2026-10-19): `DELETE /design/profiles/{id}`, which the
 * server answers by archiving it (it leaves the listing and the cascade; the mockups pinned to it
 * keep their versions). Mounted only for a stored profile the server lets the person edit
 * (`isEditable`, its `may_edit`), so the system default never shows it; the server decides again.
 *
 * It asks first (`ConfirmDialog`, focus on "Cancelar"); the delete is sent once (`retry: false`). A
 * refusal (the system default, a scope the person may only read, an id already gone) is the
 * server's sentence in Spanish, shown as an alert, and the profile stays. The parent mounts it with
 * `key` = the profile id (checklist 14), so a refusal never follows the person to another profile.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { useDeleteProfile } from "../hooks/useDesignProfiles";
import styles from "../components/ProfileEditor.module.css";

export interface ProfileDeleteContainerProps {
  readonly profileId: string;
  readonly onDeleted?: ((profileId: string) => void) | undefined;
}

export function ProfileDeleteContainer({ profileId, onDeleted }: ProfileDeleteContainerProps): ReactElement {
  const { t } = useTranslation();
  const remove = useDeleteProfile();
  const [asking, setAsking] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    if (pending) return;
    setPending(true);
    setFailure(null);
    const outcome = await remove(profileId);
    setPending(false);
    setAsking(false);
    if (!outcome.ok) {
      setFailure(userMessage(outcome.error, t("design.delete.failed")));
      return;
    }
    onDeleted?.(profileId);
  };

  return (
    <>
      <Button
        variant="danger"
        type="button"
        hasPopup="dialog"
        disabled={pending}
        onClick={() => {
          setFailure(null);
          setAsking(true);
        }}
      >
        <Icon name="trash" size="sm" />
        {t("design.delete.action")}
      </Button>
      {failure === null ? null : (
        <p className={styles.error} role="alert">
          {failure}
        </p>
      )}
      <ConfirmDialog
        open={asking}
        title={t("design.delete.title")}
        body={t("design.delete.body")}
        confirmLabel={t("design.delete.confirm")}
        cancelLabel={t("design.delete.cancel")}
        confirmLoading={pending}
        onConfirm={() => {
          void confirm();
        }}
        onCancel={() => {
          if (!pending) setAsking(false);
        }}
      />
    </>
  );
}
